<script setup lang="ts">
import {ref, toRef} from 'vue';
import {useDragHandler} from '@/composables/drag/useDragHandler.ts';
import {PulseTimeAllocation} from '@/types/DataTypes.ts';

const props = defineProps<{
  segments: Array<{
    category: any;
    value: number;
    leftPosition: number;
    width: number;
    color: string;
    canDragLeft: boolean;
    canDragRight: boolean;
  }>;
  localAllocation: PulseTimeAllocation;
  enabledCategories: any[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  'update-allocation': [key: string, value: number];
}>();

const barRef = ref<HTMLElement>();

const { onDragStart } = useDragHandler(
  toRef(props, 'localAllocation'),
  toRef(props, 'enabledCategories'),
  toRef(props, 'disabled'),
  barRef,
  (event, key, value) => emit('update-allocation', key, value)
);
</script>

<template>
  <div ref="barRef" class="relative h-10 rounded-lg overflow-hidden border border-surface-300 dark:border-surface-600 mt-2">
    <!-- Segments -->
    <div 
      v-for="(segment, index) in segments" 
      :key="segment.category.key"
      v-tooltip.top="{value: segment.category.description, showDelay: 200}"
      :class="[
        'absolute top-0 h-full transition-all duration-200 flex items-center justify-center text-xs font-medium',
        segment.category.key === 'unallocated' ? 'text-gray-500 border border-gray-300' : 'text-white'
      ]"
      :style="{
        left: `${segment.leftPosition}%`,
        width: `${segment.width}%`,
        backgroundColor: segment.color
      }"
    >
      <span v-if="segment.width >= 15" class="truncate px-1">
        {{ segment.category.label }}
      </span>
      <span v-if="segment.width >= 8" class="ml-1">{{ segment.value }}%</span>
    </div>
    
    <!-- Drag handles -->
    <div
      v-for="(segment, index) in segments.filter(s => s.category.key !== 'unallocated').slice(0, -1)"
      :key="`handle-${segment.category.key}`"
      class="absolute top-0 h-full w-2 cursor-col-resize hover:bg-primary-500 hover:bg-opacity-30 transition-colors duration-150"
      :style="{
        left: `${segment.leftPosition + segment.width - 1}%`
      }"
      @mousedown="!disabled && onDragStart($event, segments.filter(s => s.category.key !== 'unallocated').indexOf(segment), true)"
    >
      <div class="w-full h-full flex items-center justify-center">
        <div class="w-0.5 h-6 bg-white bg-opacity-70 rounded-full"></div>
      </div>
    </div>
  </div>
</template>
